import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, Search, Loader2, AlertTriangle, X } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Trash tab — deleted bookings archived from Schedule / Bookings, with
 * a Restore action. The archive is written on every hard-delete so it's
 * safe to restore anything shown here.
 */
export default function TrashTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const [purgingId, setPurgingId] = useState(null);
  const [purgingAll, setPurgingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings/trash",
        { params: q ? { q } : {} });
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load trash");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const restore = async (row) => {
    setRestoringId(row.original_id);
    try {
      await api.post(`/admin/bookings/trash/${row.original_id}/restore`);
      toast.success(`Restored — ${row.full_name || row.vehicle_name} is back on the schedule`);
      setRows((r) => r.filter((x) => x.original_id !== row.original_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setRestoringId(null); }
  };

  const purgeOne = async (row) => {
    if (!window.confirm(
      `Permanently delete this trashed booking?\n\n` +
      `${row.full_name || row.customer_email || "—"} · ${row.vehicle_name || "—"}\n\n` +
      `This cannot be undone.`)) return;
    setPurgingId(row.original_id);
    try {
      await api.delete(`/admin/bookings/trash/${row.original_id}`);
      toast.success("Booking permanently removed");
      setRows((r) => r.filter((x) => x.original_id !== row.original_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setPurgingId(null); }
  };

  const purgeAll = async () => {
    const n = rows.length;
    if (!n) return;
    const typed = window.prompt(
      `MASTER DELETE — this will permanently remove all ${n} trashed booking(s) from the Back Office.\n\n` +
      `This cannot be undone. Type DELETE to confirm.`);
    if ((typed || "").trim().toUpperCase() !== "DELETE") {
      if (typed !== null) toast.error("Master delete cancelled — you didn't type DELETE");
      return;
    }
    setPurgingAll(true);
    try {
      const r = await api.post("/admin/bookings/trash/purge-all");
      toast.success(`Trash emptied — ${r.data?.deleted ?? n} record(s) permanently removed`);
      setRows([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Master delete failed");
    } finally { setPurgingAll(false); }
  };

  return (
    <div className="space-y-4" data-testid="bo-trash-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl">Trash</h2>
          <p className="text-white/50 text-xs mt-0.5">
            Deleted bookings — restore anything by mistake in one tap, or empty the trash for good.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search customer / vehicle / id"
              data-testid="bo-trash-search"
              className="bg-black/50 border border-white/15 rounded-full pl-8 pr-3 py-1.5 text-xs text-white outline-none w-64" />
          </div>
          <button onClick={load}
            className="px-3 py-1.5 rounded-full text-[10px] label-caps border border-white/20 text-white/70 hover:bg-white/5">
            Refresh
          </button>
          <button
            onClick={purgeAll}
            disabled={purgingAll || rows.length === 0}
            data-testid="bo-trash-empty"
            title={rows.length === 0 ? "Nothing to empty" : `Permanently remove ${rows.length} record(s)`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] label-caps bg-red-500/15 border border-red-500/50 text-red-200 hover:bg-red-500/25 disabled:opacity-40">
            {purgingAll ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
            Empty trash{rows.length ? ` (${rows.length})` : ""}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-amber-400/25 bg-amber-500/[0.06] p-3 text-[11px] text-white/70 flex items-start gap-2">
        <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
        <div>
          <span className="text-amber-200 font-bold">Restore drops the booking back onto the schedule</span> using
          its original id, dates, and price. <span className="text-red-300 font-bold">Empty trash</span> permanently
          removes every row shown here from the Back Office — it cannot be undone.
        </div>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-white/40 italic border border-dashed border-white/10 rounded-xl">
          Trash is empty — nothing to restore.
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-ink-900 text-white/50 label-caps text-[9px]">
              <tr>
                <th className="text-left px-3 py-2">Deleted</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Vehicle</th>
                <th className="text-left px-3 py-2">Reserved for</th>
                <th className="text-left px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">By</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.original_id} className="border-t border-white/5 hover:bg-white/[0.02]"
                    data-testid={`bo-trash-row-${r.original_id}`}>
                  <td className="px-3 py-2 text-white/70 whitespace-nowrap">
                    {r.cancelled_at ? new Date(r.cancelled_at).toLocaleString(undefined,
                      { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                  </td>
                  <td className="px-3 py-2 text-white">{r.full_name || "—"}<div className="text-[10px] text-white/40">{r.customer_email || ""}</div></td>
                  <td className="px-3 py-2 text-white/80">{r.vehicle_name || "—"}</td>
                  <td className="px-3 py-2 text-white/70 whitespace-nowrap">
                    {(r.start_at || r.pickup_datetime)
                      ? new Date(r.start_at || r.pickup_datetime).toLocaleDateString(undefined,
                          { month: "short", day: "numeric", year: "numeric" })
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-lime-300">${Number(r.amount || 0).toFixed(0)}</td>
                  <td className="px-3 py-2 text-white/50 text-[10px]">{r.cancelled_by || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => restore(r)}
                        disabled={restoringId === r.original_id || purgingId === r.original_id}
                        data-testid={`bo-trash-restore-${r.original_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] label-caps bg-cyan text-black font-bold hover:brightness-110 disabled:opacity-40">
                        {restoringId === r.original_id
                          ? <Loader2 size={11} className="animate-spin" />
                          : <><RotateCcw size={11} /> Restore</>}
                      </button>
                      <button
                        onClick={() => purgeOne(r)}
                        disabled={restoringId === r.original_id || purgingId === r.original_id}
                        data-testid={`bo-trash-purge-${r.original_id}`}
                        title="Permanently delete"
                        className="p-1.5 rounded-full text-red-300 hover:bg-red-500/15 disabled:opacity-40">
                        {purgingId === r.original_id ? <Loader2 size={11} className="animate-spin" /> : <X size={12} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
